import { useState, useEffect } from "react";
import "@/App.css";
import { Button } from "./components/ui/button";
import { Card, CardContent } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Label } from "./components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import { Sheet, SheetContent, SheetTrigger } from "./components/ui/sheet";
import {
  Heart,
  Users,
  Shield,
  Clock,
  Home,
  Phone,
  Mail,
  MapPin,
  Menu,
  X,
  CheckCircle,
  Award,
  BookOpen,
  Car,
  Utensils,
  Sparkles,
  Pill,
  Brain,
  HandHeart,
  ChevronRight,
  Shirt,
  CreditCard,
} from "lucide-react";

// Assets
const LOGO_URL = "https://customer-assets.emergentagent.com/job_202da0fd-09c1-4ce1-a3be-af27128458be/artifacts/3qsy0dkc_951a71d2-35b2-4015-ac2e-0f17df044ae2.png";
const HERO_VIDEO = "https://customer-assets.emergentagent.com/job_202da0fd-09c1-4ce1-a3be-af27128458be/artifacts/5akbrubi_vecteezy_nursing-talking-to-patient-for-rehabilitation-healthcare_49490028.mp4";

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#home", label: "Home" },
    { href: "#services", label: "Services" },
    { href: "#training", label: "Caregiver Training" },
    { href: "#contact", label: "Contact" },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass-nav header-scrolled" : "bg-transparent"
      }`}
      data-testid="main-navigation"
      role="banner"
    >
      <div className="container-custom">
        <nav className="flex items-center justify-between h-20" role="navigation" aria-label="Main navigation">
          {/* Logo */}
          <a
            href="#home"
            onClick={(e) => {
              e.preventDefault();
              scrollToSection("#home");
            }}
            className="flex items-center gap-3"
            data-testid="logo-link"
            aria-label="Trusted Hearts Home Care LLC - Go to homepage"
          >
            <img
              src={LOGO_URL}
              alt="Trusted Hearts Home Care LLC Logo"
              className="h-20 md:h-28 w-auto -my-2"
              width="112"
              height="112"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(link.href);
                }}
                className={`nav-link text-sm font-medium transition-colors ${
                  isScrolled ? "text-[#001f3f] hover:text-[#0077b6]" : "text-white hover:text-white/80"
                }`}
                data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </a>
            ))}
            <a href="tel:614-454-9528" data-testid="nav-call-btn" aria-label="Call us at 614-454-9528">
              <Button
                className="bg-[#0077b6] hover:bg-[#005f8a] text-white rounded-full px-6 shadow-md hover:shadow-lg transition-all duration-300"
              >
                <Phone className="w-4 h-4 mr-2" aria-hidden="true" />
                614-454-9528
              </Button>
            </a>
            <a href="https://buy.stripe.com/dRmcMZfcS1d5akA6FxdZ603" target="_blank" rel="noopener noreferrer" data-testid="nav-pay-btn" aria-label="Make a payment online">
              <Button
                className="bg-[#2ecc71] hover:bg-[#27ae60] text-white rounded-full px-6 shadow-md hover:shadow-lg transition-all duration-300"
              >
                <CreditCard className="w-4 h-4 mr-2" aria-hidden="true" />
                Make a Payment
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button
                variant="ghost"
                size="icon"
                className={isScrolled ? "text-[#001f3f]" : "text-white"}
                data-testid="mobile-menu-btn"
                aria-label="Open mobile menu"
              >
                <Menu className="h-6 w-6" aria-hidden="true" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] bg-white" aria-label="Mobile navigation menu">
              <div className="flex flex-col gap-6 mt-8">
                <img
                  src={LOGO_URL}
                  alt="Trusted Hearts Home Care LLC Logo"
                  className="h-28 w-auto mx-auto"
                  width="112"
                  height="112"
                />
                <nav className="flex flex-col gap-4" aria-label="Mobile navigation">
                  {navLinks.map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={(e) => {
                        e.preventDefault();
                        scrollToSection(link.href);
                      }}
                      className="text-[#001f3f] font-medium py-2 px-4 rounded-lg hover:bg-[#e0f2f1] transition-colors"
                      data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}
                    >
                      {link.label}
                    </a>
                  ))}
                </nav>
                <a href="tel:614-454-9528" className="mt-4" data-testid="mobile-call-btn" aria-label="Call us at 614-454-9528">
                  <Button className="w-full bg-[#0077b6] hover:bg-[#005f8a] text-white rounded-full">
                    <Phone className="w-4 h-4 mr-2" aria-hidden="true" />
                    614-454-9528
                  </Button>
                </a>
                <a href="https://buy.stripe.com/dRmcMZfcS1d5akA6FxdZ603" target="_blank" rel="noopener noreferrer" className="mt-2" data-testid="mobile-pay-btn" aria-label="Make a payment online">
                  <Button className="w-full bg-[#2ecc71] hover:bg-[#27ae60] text-white rounded-full">
                    <CreditCard className="w-4 h-4 mr-2" aria-hidden="true" />
                    Make a Payment
                  </Button>
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </nav>
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center"
      data-testid="hero-section"
      aria-label="Welcome to Trusted Hearts Home Care"
    >
      {/* Video Background */}
      <div className="hero-video-container" aria-hidden="true">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="hero-video"
          aria-label="Background video showing caregiver with patient"
          title="Caregiver assisting patient"
        >
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
        <div className="absolute inset-0 hero-overlay" />
      </div>

      {/* Content */}
      <div className="container-custom relative z-10 pt-20">
        <div className="max-w-3xl">
          <h1
            className="heading-primary text-white mb-4 animate-fade-in-up"
            data-testid="hero-headline"
          >
            Trusted Hearts Home Care LLC
          </h1>
          <p
            className="text-[#7fdbff] text-lg md:text-xl font-medium tracking-wide mb-6 animate-fade-in-up animation-delay-200"
            style={{ fontFamily: 'Manrope, sans-serif' }}
            role="doc-subtitle"
          >
            Professional Care. Personal Commitment.
          </p>
          <p
            className="body-large text-white/90 mb-4 max-w-2xl animate-fade-in-up animation-delay-400"
            data-testid="hero-subtext"
          >
            Compassionate non-medical home care services designed to help individuals remain safe, comfortable, and independent in their own homes.
          </p>
          <p
            className="body-text text-white/80 mb-4 max-w-2xl animate-fade-in-up animation-delay-400"
          >
            At Trusted Hearts Home Care LLC, we provide reliable and respectful support tailored to each client's needs. Our caregivers are trained to assist with daily activities while promoting dignity, safety, and quality of life.
          </p>
          <p
            className="text-[#7fdbff] text-base md:text-lg font-semibold mb-8 animate-fade-in-up animation-delay-400"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Currently accepting Private Pay clients.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-600">
            <a href="#contact" aria-label="Request a consultation">
              <Button
                size="lg"
                className="bg-[#0077b6] hover:bg-[#005f8a] text-white rounded-full px-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
                data-testid="hero-consultation-btn"
              >
                Request Consultation
                <ChevronRight className="w-5 h-5 ml-2" aria-hidden="true" />
              </Button>
            </a>
            <a href="tel:614-454-9528" data-testid="hero-call-btn" aria-label="Call us at 614-454-9528">
              <Button
                size="lg"
                variant="outline"
                className="bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white/20 rounded-full px-8 transition-all duration-300"
              >
                <Phone className="w-5 h-5 mr-2" aria-hidden="true" />
                614-454-9528
              </Button>
            </a>
          </div>
        </div>
      </div>

    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: HandHeart,
      title: "Personal Care Assistance",
      description:
        "Compassionate support with daily activities including bathing, dressing, grooming, and mobility assistance.",
    },
    {
      icon: Users,
      title: "Companion Care",
      description:
        "Meaningful companionship and social engagement to combat loneliness and promote emotional well-being.",
    },
    {
      icon: Brain,
      title: "Dementia & Alzheimer's Support",
      description:
        "Specialized care for individuals with memory conditions, focusing on safety, routine, and dignity.",
    },
    {
      icon: Utensils,
      title: "Meal Preparation",
      description:
        "Nutritious meal planning and preparation tailored to dietary needs and personal preferences.",
    },
    {
      icon: Sparkles,
      title: "Light Housekeeping",
      description:
        "Help maintaining a clean, safe, and comfortable living environment including laundry and organization.",
    },
    {
      icon: Pill,
      title: "Medication Reminders",
      description:
        "Timely reminders to help ensure medications are taken as prescribed by healthcare providers.",
    },
    {
      icon: Car,
      title: "Shopping / Errands Assistance",
      description:
        "Assistance with grocery shopping, pharmacy pickups, errands, and transportation to essential locations as part of non-medical home care support.",
    },
    {
      icon: Shirt,
      title: "Ironing & Pressing Service",
      description:
        "Look sharp without the hassle with our professional ironing and pressing service. We carefully press your clothing so you can enjoy a clean, crisp, and polished look without spending time at home doing it yourself.",
      detailed: true,
      details: {
        perfectFor: "Work uniforms, dress shirts, slacks, special occasion outfits, and more.",
        pricing: "Priced per item (based on garment type). We take care to handle each item properly to help maintain fabric quality and ensure a neat, professional finish.",
        notes: [
          "Please let us know at drop-off which items need ironing/pressing",
          "Items should be clearly separated for this service",
          "Special instructions are welcome at drop-off",
        ],
        tagline: "Enjoy convenience and a professional finish — we'll make sure you look your best.",
      },
    },
  ];

  return (
    <section
      id="services"
      className="section-padding bg-[#f8fafc]"
      data-testid="services-section"
      aria-labelledby="services-heading"
    >
      <div className="container-custom">
        <div className="text-center mb-16">
          <p className="text-[#0077b6] text-sm font-medium tracking-wide uppercase mb-3">
            What We Offer
          </p>
          <h2 id="services-heading" className="heading-secondary text-[#001f3f] mb-4">
            Our Home Care Services
          </h2>
          <p className="body-text max-w-2xl mx-auto">
            We provide comprehensive non-medical home care services designed to
            support your loved ones with dignity, compassion, and professionalism.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" role="list">
          {services.map((service, index) => (
            <Card
              key={index}
              className={`service-card bg-white border border-gray-100 hover:border-[#0077b6]/20 rounded-2xl overflow-hidden ${service.detailed ? 'md:col-span-2 lg:col-span-3' : ''}`}
              data-testid={`service-card-${index}`}
              role="listitem"
            >
              <CardContent className="p-6 md:p-8">
                <div className="service-icon w-14 h-14 rounded-xl bg-[#e0f2f1] flex items-center justify-center mb-5" aria-hidden="true">
                  <service.icon className="w-7 h-7 text-[#0077b6]" />
                </div>
                <h3 className="text-xl font-semibold text-[#001f3f] mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {service.title}
                </h3>
                <p className="body-text">{service.description}</p>
                {service.detailed && service.details && (
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm font-semibold text-[#001f3f] mb-1">Perfect For:</p>
                      <p className="body-text text-sm">{service.details.perfectFor}</p>
                      <p className="text-sm font-semibold text-[#001f3f] mt-3 mb-1">Pricing:</p>
                      <p className="body-text text-sm">{service.details.pricing}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#001f3f] mb-2">Please Note:</p>
                      <ul className="space-y-1.5">
                        {service.details.notes.map((note, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                            <CheckCircle className="w-4 h-4 text-[#0077b6] mt-0.5 flex-shrink-0" />
                            <span>{note}</span>
                          </li>
                        ))}
                      </ul>
                      <p className="mt-3 text-sm font-medium text-[#0077b6] italic">{service.details.tagline}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Service Note */}
        <div className="mt-8 p-6 bg-[#e0f2f1] rounded-2xl" role="note">
          <p className="text-sm text-[#004d40] text-center">
            <span className="font-medium">Note:</span> Shopping and errands assistance includes transportation to essential locations and is billed at the standard hourly caregiver rate.
          </p>
        </div>
      </div>
    </section>
  );
};

// Caregiver Training Section
const CaregiverTrainingSection = () => {
  const trainingItems = [
    "Ongoing training and orientation",
    "HIPAA and confidentiality training",
    "Abuse prevention and reporting education",
    "Infection control procedures",
    "Safety and emergency protocol training",
  ];

  const specializedTraining = [
    "Memory loss",
    "Dementia",
    "Alzheimer's-related conditions",
  ];

  return (
    <section
      id="training"
      className="section-padding bg-white"
      data-testid="training-section"
      aria-labelledby="training-heading"
    >
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Image */}
          <div className="relative">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="Professional caregiver training session with healthcare team members collaborating"
                className="w-full h-full object-cover"
                width="800"
                height="600"
                loading="lazy"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-[#001f3f] text-white p-6 rounded-2xl shadow-lg hidden md:block" aria-hidden="true">
              <BookOpen className="w-8 h-8 mb-2" />
              <p className="font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>Continuous</p>
              <p className="text-sm text-white/80">Education</p>
            </div>
          </div>

          {/* Content */}
          <div>
            <p className="text-[#0077b6] text-sm font-medium tracking-wide uppercase mb-3">
              Excellence in Care
            </p>
            <h2 id="training-heading" className="heading-secondary text-[#001f3f] mb-6">
              Caregiver Training & Memory Support
            </h2>
            <p className="body-large text-[#001f3f]/80 mb-8">
              At Trusted Hearts Home Care LLC, we prioritize continuous caregiver
              education and professional development.
            </p>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-[#001f3f] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Our caregivers receive:
              </h3>
              <ul className="space-y-3" role="list">
                {trainingItems.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#0077b6] flex-shrink-0 mt-0.5" aria-hidden="true" />
                    <span className="text-[#001f3f]/80">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-[#f8fafc] p-6 rounded-2xl">
              <h3 className="text-lg font-semibold text-[#001f3f] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Specialized training for:
              </h3>
              <div className="flex flex-wrap gap-3" role="list" aria-label="Specialized training areas">
                {specializedTraining.map((item, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 bg-white border border-[#0077b6]/20 rounded-full text-sm text-[#001f3f] font-medium"
                    role="listitem"
                  >
                    {item}
                  </span>
                ))}
              </div>
              <p className="mt-4 text-sm text-[#001f3f]/70">
                Our approach emphasizes patience, structure, communication, and
                maintaining dignity for every client.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Trust & Safety Section
const TrustSafetySection = () => {
  const trustItems = [
    {
      icon: Shield,
      title: "Background-Checked",
      description: "All caregivers undergo comprehensive background screening",
    },
    {
      icon: Award,
      title: "Professionally Trained",
      description: "Rigorous training programs ensure quality care",
    },
    {
      icon: Heart,
      title: "Compassionate Care",
      description: "Patient-focused approach with genuine empathy",
    },
    {
      icon: Clock,
      title: "Reliable Service",
      description: "Dependable and consistent care you can count on",
    },
  ];

  return (
    <section
      className="section-padding bg-[#001f3f]"
      data-testid="trust-section"
      aria-labelledby="trust-heading"
    >
      <div className="container-custom">
        <div className="text-center mb-16">
          <p className="text-[#7fdbff] text-sm font-medium tracking-wide uppercase mb-3">
            Why Choose Us
          </p>
          <h2 id="trust-heading" className="heading-secondary text-white mb-4">
            Trust & Safety First
          </h2>
          <p className="text-white/80 max-w-2xl mx-auto">
            Your loved ones deserve the highest standard of care. We ensure every
            caregiver meets our rigorous requirements.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" role="list">
          {trustItems.map((item, index) => (
            <div
              key={index}
              className="trust-badge bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 text-center"
              data-testid={`trust-badge-${index}`}
              role="listitem"
            >
              <div className="w-16 h-16 rounded-full bg-[#0077b6] flex items-center justify-center mx-auto mb-4" aria-hidden="true">
                <item.icon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {item.title}
              </h3>
              <p className="text-sm text-white/70">{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Service Area Section
const ServiceAreaSection = () => {
  return (
    <section
      className="section-padding bg-white"
      data-testid="service-area-section"
      aria-labelledby="service-area-heading"
    >
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div>
            <p className="text-[#0077b6] text-sm font-medium tracking-wide uppercase mb-3">
              Service Coverage
            </p>
            <h2 id="service-area-heading" className="heading-secondary text-[#001f3f] mb-6">
              Serving Dublin, Columbus & Surrounding Areas
            </h2>
            <p className="body-large text-[#001f3f]/80 mb-6">
              Trusted Hearts Home Care LLC proudly provides non-medical in-home
              care services throughout Dublin, Columbus, and surrounding Central
              Ohio communities.
            </p>
            <address className="not-italic flex items-start gap-3 mb-8">
              <MapPin className="w-6 h-6 text-[#0077b6] flex-shrink-0 mt-1" aria-hidden="true" />
              <div>
                <p className="font-semibold text-[#001f3f]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  6397 Emerald Parkway Suite 210
                </p>
                <p className="text-[#001f3f]/70">Dublin, Ohio</p>
              </div>
            </address>
            <div className="flex flex-wrap gap-3" role="list" aria-label="Service areas">
              {["Dublin", "Columbus", "Hilliard", "Powell", "Westerville", "Grove City"].map(
                (area, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 bg-[#e0f2f1] rounded-full text-sm text-[#004d40] font-medium"
                    role="listitem"
                  >
                    {area}
                  </span>
                )
              )}
            </div>
          </div>

          {/* Map */}
          <div className="map-container aspect-[4/3] lg:aspect-square">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3054.8644372426855!2d-83.11634492397377!3d40.09997837151485!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88388e4f0e8b9c5f%3A0x1234567890abcdef!2s6397%20Emerald%20Pkwy%20%23210%2C%20Dublin%2C%20OH%2043016!5e0!3m2!1sen!2sus!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Trusted Hearts Home Care LLC Location - 6397 Emerald Parkway Suite 210, Dublin, Ohio"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData((prev) => ({ ...prev, service: value }));
  };

  // Encode form data for Netlify submission
  const encode = (data) => {
    return Object.keys(data)
      .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
      .join("&");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({
          "form-name": "contact",
          ...formData
        })
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({
          name: "",
          phone: "",
          email: "",
          service: "",
          message: "",
        });
        setTimeout(() => setIsSubmitted(false), 5000);
      } else {
        throw new Error("Form submission failed");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setSubmitError("There was an error submitting the form. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    "Personal Care Assistance",
    "Companion Care",
    "Dementia & Alzheimer's Support",
    "Meal Preparation",
    "Light Housekeeping",
    "Medication Reminders",
    "Shopping / Errands Assistance",
    "Other",
  ];

  return (
    <section
      id="contact"
      className="section-padding bg-[#f8fafc]"
      data-testid="contact-section"
      aria-labelledby="contact-heading"
    >
      <div className="container-custom">
        <div className="text-center mb-16">
          <p className="text-[#0077b6] text-sm font-medium tracking-wide uppercase mb-3">
            Get In Touch
          </p>
          <h2 id="contact-heading" className="heading-secondary text-[#001f3f] mb-4">
            Request a Consultation
          </h2>
          <p className="body-text max-w-2xl mx-auto">
            Ready to learn more about our home care services? Fill out the form
            below and we'll be in touch soon.
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <Card className="bg-white border-0 shadow-xl rounded-2xl overflow-hidden">
            <CardContent className="p-8 md:p-10">
              {/* Netlify Forms */}
              <form
                name="contact"
                method="POST"
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                onSubmit={handleSubmit}
                className="space-y-6"
                data-testid="contact-form"
                aria-label="Contact form"
              >
                {/* Hidden fields for Netlify */}
                <input type="hidden" name="form-name" value="contact" />
                <p className="hidden">
                  <label>
                    Don't fill this out if you're human:{" "}
                    <input name="bot-field" tabIndex="-1" autoComplete="off" />
                  </label>
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-sm font-medium text-gray-700 mb-1.5 block">
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="form-input bg-gray-50 border-gray-200 focus:border-[#0077b6] focus:ring-[#0077b6]/20 rounded-lg"
                      placeholder="Your name"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-sm font-medium text-gray-700 mb-1.5 block">
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="form-input bg-gray-50 border-gray-200 focus:border-[#0077b6] focus:ring-[#0077b6]/20 rounded-lg"
                      placeholder="(555) 123-4567"
                      data-testid="contact-phone-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="form-input bg-gray-50 border-gray-200 focus:border-[#0077b6] focus:ring-[#0077b6]/20 rounded-lg"
                    placeholder="your@email.com"
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="service" className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Service Needed
                  </Label>
                  <Select
                    name="service"
                    value={formData.service}
                    onValueChange={handleServiceChange}
                  >
                    <SelectTrigger
                      className="bg-gray-50 border-gray-200 focus:border-[#0077b6] focus:ring-[#0077b6]/20 rounded-lg"
                      data-testid="contact-service-select"
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((service, index) => (
                        <SelectItem key={index} value={service}>
                          {service}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* Hidden input for Netlify form submission */}
                  <input type="hidden" name="service" value={formData.service} />
                </div>

                <div>
                  <Label htmlFor="message" className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Message
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    className="form-input bg-gray-50 border-gray-200 focus:border-[#0077b6] focus:ring-[#0077b6]/20 rounded-lg resize-none"
                    placeholder="Tell us about your care needs..."
                    data-testid="contact-message-input"
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 bg-[#001f3f] hover:bg-[#001f3f]/90 text-white rounded-full py-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Mail className="w-5 h-5 mr-2" />
                        Submit Request
                      </>
                    )}
                  </Button>
                  <a href="tel:614-454-9528" className="flex-1">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full border-[#001f3f] text-[#001f3f] hover:bg-[#001f3f]/5 rounded-full py-6 transition-all duration-300"
                      data-testid="contact-call-btn"
                    >
                      <Phone className="w-5 h-5 mr-2" />
                      614-454-9528
                    </Button>
                  </a>
                </div>
              </form>

              {isSubmitted && (
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 success-checkmark" />
                  <p className="text-green-800 font-medium">
                    Thank you! We'll be in touch soon.
                  </p>
                </div>
              )}

              {submitError && (
                <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
                  <X className="w-6 h-6 text-red-600" />
                  <p className="text-red-800 font-medium">
                    {submitError}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Private Pay Notice */}
          <p className="text-center text-sm text-[#001f3f]/60 mt-6">
            Currently accepting Private Pay only.
          </p>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = [
    { href: "#home", label: "Home" },
    { href: "#services", label: "Services" },
    { href: "#training", label: "Caregiver Training" },
    { href: "#contact", label: "Contact" },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer className="bg-[#001f3f] text-white" data-testid="footer" role="contentinfo">
      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-4 mb-6">
              <img
                src={LOGO_URL}
                alt="Trusted Hearts Home Care LLC Logo"
                className="h-28 w-auto"
                width="112"
                height="112"
                loading="lazy"
              />
              <div>
                <p className="font-semibold text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Trusted Hearts Home Care LLC
                </p>
                <p className="text-sm text-white/70">
                  Professional Care. Personal Commitment.
                </p>
              </div>
            </div>
            <p className="text-white/70 mb-6 max-w-md">
              Providing compassionate, reliable non-medical home care services
              throughout Dublin, Columbus, and surrounding Central Ohio communities.
            </p>
            <address className="not-italic">
              <div className="flex items-center gap-3 text-white/80 mb-3">
                <MapPin className="w-5 h-5 text-[#7fdbff]" aria-hidden="true" />
                <span>6397 Emerald Parkway Suite 210, Dublin, Ohio</span>
              </div>
              <div className="flex items-center gap-3 text-white/80 mb-3">
                <Phone className="w-5 h-5 text-[#7fdbff]" aria-hidden="true" />
                <a href="tel:614-454-9528" className="hover:text-white transition-colors" aria-label="Call us at 614-454-9528">614-454-9528</a>
              </div>
              <div className="flex items-center gap-3 text-white/80">
                <Mail className="w-5 h-5 text-[#7fdbff]" aria-hidden="true" />
                <a href="mailto:info@trustedheartshomecare.co" className="hover:text-white transition-colors" aria-label="Email us at info@trustedheartshomecare.co">info@trustedheartshomecare.co</a>
              </div>
            </address>
          </div>

          {/* Quick Links */}
          <nav aria-label="Footer navigation">
            <h3 className="font-semibold text-lg mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Quick Links
            </h3>
            <ul className="space-y-3" role="list">
              {footerLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(link.href);
                    }}
                    className="footer-link text-white/70 hover:text-white transition-colors"
                    data-testid={`footer-link-${link.label.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Service Area */}
          <div>
            <h3 className="font-semibold text-lg mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Service Area
            </h3>
            <ul className="space-y-2 text-white/70" role="list" aria-label="Service areas">
              <li>Dublin</li>
              <li>Columbus</li>
              <li>Hilliard</li>
              <li>Powell</li>
              <li>Westerville</li>
              <li>Central Ohio</li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/60 text-sm">
            &copy; {currentYear} Trusted Hearts Home Care LLC. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <a href="tel:614-454-9528" className="text-white/60 hover:text-white transition-colors" aria-label="Call us">
              <Phone className="w-5 h-5" aria-hidden="true" />
            </a>
            <a href="mailto:info@trustedheartshomecare.co" className="text-white/60 hover:text-white transition-colors" aria-label="Email us">
              <Mail className="w-5 h-5" aria-hidden="true" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="App" data-testid="app-container">
      <Navigation />
      <main>
        <HeroSection />
        <ServicesSection />
        <CaregiverTrainingSection />
        <TrustSafetySection />
        <ServiceAreaSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
